import { useContext } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { useLocation } from "@gatsbyjs/reach-router";

import LazyLoad from "react-lazyload";

import { GeoContext } from "../context/GeoProvider";
import { gaCustomEvent } from "@helpers/browser";
import { edgesToArray } from "@helpers/general";

import { withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";

import { ALLink as Link } from "./ALComponents";
import AutoScroll from "./al_components/AutoScroll";

function QuickAccess() {
  const { gePriceDetails } = useContext(GeoContext);
  const location = useLocation();

  const onMenuClick = (label) => {
    gaCustomEvent("Menu Link - Quick Access With Image", "Click", label);
  };

  const renderQuickAccessMenu = (content) => {
    const { quickAccessEntries } = content;

    let slug = null;
    if (location?.pathname) {
      const path = location.pathname.split("/").filter((part) => part);
      slug = path[path.length - 1];
    }

    const result = quickAccessEntries
      ?.map((entry) => {
        const { id, title, tag, tagBackgroundColor, handle } = entry;
        const to = handle;
        const imageSrc = getImage(entry.image.mobileImage.gatsbyImageData);
        const altText = entry?.image?.mobileImage?.altText || title;

        if (!handle) {
          return null;
        }

        return (
          <Link
            key={id}
            className="inline-flex relative flex-col items-start pb-0 pt-[15px]"
            to={to}
            onClick={() => onMenuClick(title)}
          >
            {tag && tag !== "null" && (
              <span
                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 py-[2px] px-[4.5px] typo-micro font-bold text-white rounded z-50 whitespace-nowrap"
                style={{ backgroundColor: tagBackgroundColor }}
              >
                {tag}
              </span>
            )}
            <GatsbyImage
              loading="eager"
              image={imageSrc}
              alt={altText}
              className="w-[62px] h-[72px] inline-block object-cover"
              style={tag && tag !== "null" ? { border: `1px solid ${tagBackgroundColor}` } : {}}
            />
            <span
              className={`px-0 pt-1 typo-micro font-bold whitespace-nowrap ${
                slug === handle ? "bold" : ""
              }`}
              style={{ color: content?.textColor || "#000000" }}
            >
              {title}
            </span>
          </Link>
        );
      })
      ?.filter(Boolean);

    return (
      <LazyLoad offset={0} height={111} className="md:hidden">
        <AutoScroll defaultPadding={7}>
          <div className="h-full w-screen flex flex-row justify-between overflow-x-scroll no-scrollbar">
            {result}
          </div>
        </AutoScroll>
      </LazyLoad>
    );
  };

  const data = useStaticQuery(graphql`
    query QuickAccessMenu {
      allContentfulQuickAccess {
        edges {
          node {
            node_locale
            quickAccessEntries {
              contentful_id
              image {
                altText
                mobileImage {
                  gatsbyImageData(width: 72)
                }
              }
              id
              title
              tag
              tagBackgroundColor
              handle
            }
            countries {
              code
            }
            textColor
          }
        }
      }
    }
  `);

  let quickAccess = null;
  if (data.allContentfulQuickAccess?.edges && data.allContentfulQuickAccess?.edges.length > 0) {
    const allQuickAccess = edgesToArray(data.allContentfulQuickAccess);
    const defaultMenu = allQuickAccess.find((n) => n?.countries?.some((c) => c.code === "RoW"));
    const localeSpecificQuickAccess = allQuickAccess.filter((n) =>
      n?.countries?.some((c) => c.code === gePriceDetails?.CountryCode)
    );
    const localeSpecificQuickAccessRoot = localeSpecificQuickAccess.find(
      (b) => b.node_locale === gePriceDetails?.Culture
    );
    quickAccess = localeSpecificQuickAccessRoot ?? defaultMenu;
  }

  if (quickAccess) {
    return renderQuickAccessMenu(quickAccess);
  }

  return null;
}

export default withALErrorBoundary({
  name: "QuickAccess",
  priority: "P3",
})(QuickAccess);
